export type ChartPalette = {
  green: string;
  yellow: string;
  orange: string;
  red: string;
};

/**
 * The app's long-standing full-strength traffic-light palette. Nothing renders
 * it directly any more — it is the base every softened palette below is derived
 * from, and the fallback if the softer look is ever pulled back.
 */
export const FULL_PALETTE: ChartPalette = {
  green: '#78F880',
  yellow: '#FEFA83',
  orange: '#FFD37E',
  red: '#FC6552',
};

/**
 * FULL_PALETTE at `alpha` opacity over white, flattened to opaque hex.
 *
 * Softening by opacity rather than by mixing toward a desaturated tint is
 * deliberate: it keeps the original hue and its bright traffic-light character,
 * where desaturating turns the colors gray and muddy.
 *
 * Flattening rather than emitting rgba() is also deliberate — the result is
 * identical over the white backgrounds these sit on, and solid hex rasterizes
 * predictably in the html2canvas PDF export.
 *
 * Lower alpha = lighter. 1.0 returns FULL_PALETTE exactly.
 */
export function softenPalette(alpha: number): ChartPalette {
  const soften = (hex: string) =>
    '#' + [1, 3, 5]
      .map(i => Math.round(255 - alpha * (255 - parseInt(hex.substr(i, 2), 16))))
      .map(c => c.toString(16).padStart(2, '0').toUpperCase())
      .join('');
  return {
    green: soften(FULL_PALETTE.green),
    yellow: soften(FULL_PALETTE.yellow),
    orange: soften(FULL_PALETTE.orange),
    red: soften(FULL_PALETTE.red),
  };
}

/**
 * The default palette: every gauge arc, reference-range band, BMI band, and
 * section-panel fill. Wide blocks of color on white.
 *
 *   green #83F98A · yellow #FEFA8D · orange #FFD788 · red #FC7160
 */
export const MUTED_PALETTE = softenPalette(0.92);

/**
 * Results Overview per-section marker-distribution bars. Held a notch DARKER
 * than MUTED_PALETTE because these segments are only 7px tall inside a busy
 * card — at the default alpha the green and yellow slivers washed out.
 *
 *   green #7FF886 · yellow #FEFA89 · orange #FFD584 · red #FC6D5B
 */
export const MARKER_BAR_PALETTE = softenPalette(0.95);

/**
 * Comparison-table cell tints. Held a notch LIGHTER than MUTED_PALETTE: that
 * table packs many tinted cells close together, so the full-strength wash is
 * overwhelming across a whole grid.
 *
 *   green #86F98D · yellow #FEFB8F · orange #FFD78B · red #FC7463
 *
 * These values are duplicated by hand as `.bg-green/.bg-yellow/.bg-orange/
 * .bg-red` in src/index.css, since the table tints via CSS class rather than
 * inline style — keep the two in sync. The comparison PDF picks up those
 * classes verbatim, so a change lands on screen and in the export together.
 */
export const TABLE_CELL_PALETTE = softenPalette(0.90);

/*
 * Two palettes deliberately do NOT track these:
 *  - the "Markers Outside Optimal" mini-chart pastels
 *    (#D4EBD5/#F6F0C4/#F9E0C4/#F6CEC6), kept pale on purpose so a long stacked
 *    wall of bars reads calmly;
 *  - the Results Overview STATUS_SOFT pill/tally palette, which needs far more
 *    contrast because text sits directly on top of it.
 */

/**
 * `a` and `b` mixed, `t` of the way from `a` to `b`. Opaque hex in, opaque
 * hex out, for the same reason softenPalette() flattens: solid hex rasterizes
 * predictably in the html2canvas PDF export.
 */
function mixHex(a: string, b: string, t: number): string {
  return '#' + [1, 3, 5]
    .map(i => {
      const from = parseInt(a.substr(i, 2), 16);
      const to = parseInt(b.substr(i, 2), 16);
      return Math.round(from + (to - from) * t);
    })
    .map(c => c.toString(16).padStart(2, '0').toUpperCase())
    .join('');
}

/**
 * The deep end of the MVX severity ramp.
 *
 * Dr. Schmidt, 2026-10-13, on where the alarm starts: "All these numbers are
 * gradiently bad. Shade into red starting at 62", and for IVX "make the red
 * darker starting at 58". So the three bands above optimal are not three flat
 * colors any more, they are one ramp that reaches red at the lab limit and
 * keeps deepening past it (see MvxSeverityBar).
 *
 * MUTED red at 70% over a near-black, which darkens it without turning it
 * brown: the hue has to stay the alarm red the rest of the app uses.
 */
export const SEVERITY_DEEP_RED = mixHex(MUTED_PALETTE.red, '#3A0A04', 0.3);

/**
 * The good end of the MVX severity ramp, for the bars where one direction is
 * better all the way.
 *
 * Dr. Schmidt on MVX and IVX: "Get it as low as you can. The lowest I have
 * seen is 13." A flat green from 0 to the target says a 33 is as good as a
 * 13, which is the same thing the fade above the target was added to stop
 * saying on the other side (Maryann, 2026-10-14).
 *
 * Held to 30% of the way toward a forest green: deep enough to read as
 * "keep going", light enough that the black blood drop still shows on it.
 */
export const SEVERITY_DEEP_GREEN = mixHex(MUTED_PALETTE.green, '#0B5D1E', 0.3);

/**
 * How green a Lab band ever gets, at the edge that touches Optimal, as a
 * share of the way from yellow back to green. HALF.
 *
 * The optimal bounds are 1 SD either side of the mean, a statistical line
 * rather than a measured one, so a value a hair outside optimalMax is not
 * meaningfully different from one a hair inside, and the hard green/yellow
 * cliff overstated the difference. The fade is the MVX severity bar's
 * gradient green band (MvxSeverityBar, Dr. Schmidt 2026-10-08) run the other
 * way: there the green gives way to yellow as a value drifts off target,
 * here the yellow leans back toward green as a value approaches optimal.
 *
 * Deliberately NOT 1. At full green a Lab band's inner edge and the Optimal
 * band's outer edge are the same color, and optimalMin/optimalMax — the
 * most-read line on these charts — would survive only as the 1px grid line.
 * Band names print on the first row of a panel only, so every other row
 * reads by color alone. Maryann, 2026-10-08: "a lot less green. And more
 * yellow."
 *
 * Only the green/yellow transition fades. labMin/labMax are the lab's own
 * printed reference limits, a real externally defined edge rather than a
 * derived one, so they stay crisp steps — and fading every boundary in turn
 * would leave one continuous spectrum with no readable zones at all.
 */
export const LAB_FADE_GREEN_SHARE = 0.5;

/**
 * How far across a Lab band the green survives before the fill is pure
 * yellow, measured from the Optimal edge.
 *
 * The band is ~87px wide at the section panel's 760px and wider again in the
 * 1028px PDF, so half of it is a legible ramp rather than a smudge, while the
 * outer half still reads as flat Lab yellow.
 */
export const LAB_FADE_REACH = 0.55;

/**
 * The green-leaning color at a Lab band's Optimal edge.
 *
 * Derived from MUTED_PALETTE rather than written as a literal so it tracks
 * the palette if softenPalette()'s alpha ever moves.
 *
 *   #C1FA8C
 *
 * The ramp between these two is unusually clean: MUTED_PALETTE's green and
 * yellow differ almost entirely in the red channel (131 → 254) with green and
 * blue effectively equal, so the blend is a hue sweep at constant lightness
 * with no muddy midpoint and no brightness dip.
 */
export const LAB_FADE_FROM = mixHex(MUTED_PALETTE.yellow, MUTED_PALETTE.green, LAB_FADE_GREEN_SHARE);

/** Structurally a Highcharts.GradientColorObject, without the import. */
export type BandGradient = {
  linearGradient: { x1: number; y1: number; x2: number; y2: number };
  stops: Array<[number, string]>;
};

/**
 * The fill for one Lab band: flat yellow at its outer edge, leaning green at
 * the edge that touches Optimal.
 *
 * `optimalSide` is which side of THIS band the Optimal band sits on, which is
 * the side the green goes. The two Lab bands of a symmetric chart are mirror
 * images of one another ('right' for the low band, 'left' for the high one);
 * the one-sided shapes each have their own orientation, RDW drawing Optimal
 * to the left of its single Lab band and eGFR to the right.
 *
 * The stop fractions are relative to the band's OWN bounding box, which is
 * what a Highcharts gradient takes, so this stays one band: its centered name
 * label, the grid lines and the band structure are all unchanged. x1→x2 runs
 * left to right because these are inverted bar charts, where the y-axis is
 * the horizontal one.
 *
 * Display only. Every status, count, tally and table tint still reads the
 * hard boundary, so nothing newly flags and nothing turns green.
 */
/**
 * The band immediately past a ceiling that optimal and lab share.
 *
 * Small HDL-P tops out at 21.6 on both bounds, so there is no Lab band above
 * optimal to carry the usual yellow and the next band along is "> Lab". A
 * flat orange there would step straight out of green, which overstates a
 * side the evidence says nothing about. This ramps instead: yellow where it
 * leaves the ceiling, orange by the far edge (Maryann, 2026-10-15).
 */
export function pastCeilingFill(optimalSide: 'left' | 'right'): BandGradient {
  const { yellow: Y, orange: O } = MUTED_PALETTE;
  const stops: Array<[number, string]> = optimalSide === 'left'
    ? [[0, Y], [1, O]]
    : [[0, O], [1, Y]];
  return { linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 }, stops };
}

export function labBandFill(optimalSide: 'left' | 'right'): BandGradient {
  const Y = MUTED_PALETTE.yellow;
  // Rounded, or the mirrored band's middle stop serializes into the SVG as
  // 0.44999999999999996.
  const mirrored = Math.round((1 - LAB_FADE_REACH) * 1e4) / 1e4;
  const stops: Array<[number, string]> = optimalSide === 'left'
    ? [[0, LAB_FADE_FROM], [LAB_FADE_REACH, Y], [1, Y]]
    : [[0, Y], [mirrored, Y], [1, LAB_FADE_FROM]];
  return { linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 }, stops };
}

/* ------------------------------------------------------------------ *
 * THE MVX SEVERITY RAMP
 *
 * One definition, two shapes. The bar graphs hand these stops straight to
 * Highcharts as a linear gradient; the hero gauge samples them along its arc
 * (a linear gradient across a circle reads as a lighting error, so the gauge
 * paints a run of thin arcs instead). Both then shade identically, which is
 * the point: the same score is the same color whether the practitioner is
 * reading the gauge or the bar under it (Maryann, 2026-10-16).
 *
 * Dr. Schmidt, 2026-10-13: "All these numbers are gradiently bad. Shade into
 * red starting at 62", and for IVX "make the red darker starting at 58". So
 * the three bands above optimal are not three flat colors, they are one ramp
 * that reaches red just past the lab limit and keeps deepening.
 *
 * ⚠ DISPLAY ONLY. The zones are unchanged and every status, word and tally
 * still reads the hard boundary. He described a color, not a threshold.
 * ------------------------------------------------------------------ */

/**
 * How far through the fade the Optimal band has finished turning yellow, as a
 * share of the stretch from the target to the band edge. 0.6 leaves the last
 * 40% of that stretch sitting at yellow, so the Borderline tick has yellow on
 * the optimal side of it rather than arriving at yellow exactly on the line.
 */
export const SEVERITY_GREEN_FADE_REACH = 0.6;

/**
 * How much of the Lab band stays yellow before it starts turning orange. The
 * other side of the same point: yellow continues past the Borderline tick, so
 * the tick sits in the middle of a yellow stretch instead of at the end of
 * one. (Both figures: Maryann, 2026-10-14, reading the bars.)
 */
export const SEVERITY_LAB_YELLOW_HOLD = 0.4;

/**
 * Where in the Lab band the ramp has finished arriving at orange, so the run
 * up to the Lab tick is orange rather than still on its way there (Maryann,
 * 2026-10-14). Orange then holds to the tick.
 */
export const SEVERITY_LAB_ORANGE_REACH = 0.7;

/**
 * How far past the Lab tick the ramp takes to arrive at full red, as a share
 * of the band between the Lab tick and the Alarm tick. Red then holds to the
 * Alarm tick and deepens from there.
 *
 * Just under half, so the stretch immediately past the lab limit reads red
 * rather than orange on its way (Maryann, 2026-10-14: "make it a little more
 * red on the right side of the lab tick"). It is also the closest the paint
 * gets to his "shade into red starting at 62".
 */
export const SEVERITY_ABOVE_LAB_RED_REACH = 0.45;

/** Which band of the four, counted away from the good end. */
export type SeverityBand = 0 | 1 | 2 | 3;

/**
 * One band's stops, as fractions of that band, running from its good edge to
 * its bad edge.
 *
 * `targetFrac` applies to band 0 only: where the target sits inside Optimal,
 * as a fraction of the band. Without one the whole band is a single sweep
 * from deep green at the floor to plain green at the edge, because the good
 * end is still the far end (Dr. Schmidt on MVX and IVX: "get it as low as you
 * can"). With one, the sweep ends at the target and the rest fades to yellow,
 * so the bar stops saying a 299 is as good as a 259.
 */
export function severityBandStops(band: SeverityBand, targetFrac?: number): Array<[number, string]> {
  const { green: G, yellow: Y, orange: O, red: R } = MUTED_PALETTE;
  switch (band) {
    case 0:
      if (targetFrac === undefined) return [[0, SEVERITY_DEEP_GREEN], [1, G]];
      return [
        [0, SEVERITY_DEEP_GREEN],
        [targetFrac, G],
        [targetFrac + (1 - targetFrac) * SEVERITY_GREEN_FADE_REACH, Y],
        [1, Y],
      ];
    // Lab: yellow carries on past the Borderline tick, then orange, which
    // holds into the Lab tick.
    case 1:
      return [[0, Y], [SEVERITY_LAB_YELLOW_HOLD, Y], [SEVERITY_LAB_ORANGE_REACH, O], [1, O]];
    // Above the lab range: orange on the tick, red just past it.
    case 2:
      return [[0, O], [SEVERITY_ABOVE_LAB_RED_REACH, R], [1, R]];
    // Alarm: red at the tick, deepening to the end.
    default:
      return [[0, R], [1, SEVERITY_DEEP_RED]];
  }
}

/**
 * The ramp's color `t` of the way across one band, interpolated between its
 * stops. Opaque hex out, like everything else here, so it rasterizes
 * predictably in the html2canvas PDF export.
 */
export function severityRampColor(band: SeverityBand, t: number, targetFrac?: number): string {
  const stops = severityBandStops(band, targetFrac);
  const at = Math.min(1, Math.max(0, t));
  for (let i = 1; i < stops.length; i++) {
    const [p0, c0] = stops[i - 1];
    const [p1, c1] = stops[i];
    if (at <= p1) return p1 === p0 ? c1 : mixHex(c0, c1, (at - p0) / (p1 - p0));
  }
  return stops[stops.length - 1][1];
}
